'use client';

import { type CSSProperties, useId } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { cn } from '@kit/ui/utils';

import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';

interface SessionOption {
  id: string;
  booking_count: number;
}

interface PackageSessionOptionSelectProps {
  options: SessionOption[];
  value: string;
  onValueChange: (packageId: string) => void;
  /** Options that cannot be bought (sold out); shown but not selectable. */
  disabledIds?: ReadonlySet<string>;
  className?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
}

/**
 * "Number of sessions" picker for a package sold at several session counts.
 * Each option is a separate package, so the value is the chosen package id.
 */
export function PackageSessionOptionSelect({
  options,
  value,
  onValueChange,
  disabledIds,
  className,
  contentClassName,
  contentStyle,
}: PackageSessionOptionSelectProps) {
  const { t } = useTranslation('bookings');
  const presentation = useNativePresentation();
  const triggerId = useId();

  return (
    // Only clicks are contained (a card-wide onClick must not fire while
    // choosing); keys pass through so the surrounding card stays operable.
    <div
      role="group"
      className={cn('space-y-1', className)}
      onClick={(event) => event.stopPropagation()}
      data-test="package-session-option-select"
    >
      <label
        htmlFor={triggerId}
        className="text-muted-foreground block text-xs font-medium"
      >
        {t('packageSessionOptions.label')}
      </label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={triggerId} className="h-11 w-full lg:h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          className={contentClassName ?? presentation.className}
          style={contentStyle ?? presentation.style}
        >
          {options.map((option) => (
            <SelectItem
              key={option.id}
              value={option.id}
              disabled={disabledIds?.has(option.id)}
              data-test={`package-session-option-${option.booking_count}`}
            >
              {t('packageSessionOptions.option', {
                count: option.booking_count,
              })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
